/* PavCon, LLC Proprietary.All rights reserved 2022 */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IState } from '../store/root.store';
import { AppUserActions, NAMESPACE } from '../store/app-user.reducer';
import { LocalStorageService } from './local-storage.service';
import { AppSessionUser } from '../domain/app-session-user.entity';
import { environment } from '../../../environments/environment';

const AmazonCognitoIdentity = require('amazon-cognito-identity-js');

@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {
  private isInitialized = false;

  constructor(
    private store: Store<IState>,
    private localStorage: LocalStorageService
  ) {}


  async initializeApp(): Promise<void> {
    try {

      await this.restoreSession();
      this.initializeStores();
      this.isInitialized = true;
    } catch (error) {
      console.error('Error during app initialization:', error);
      this.isInitialized = true;
    }
  }


  private async restoreSession(): Promise<void> {
    const local = this.localStorage?.get(NAMESPACE) as AppSessionUser;

    if (!local || typeof local !== 'object') {
      return;
    }


    const isValid = await this.validateCognitoSession();

    if (isValid) {

      this.store.dispatch({
        type: AppUserActions.SET_SESSION_USER,
        payload: local
      });
    } else {
      this.clearSession();
    }
  }


  private async validateCognitoSession(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const poolData = {
          UserPoolId: environment.userPoolId,
          ClientId: environment.clientId,
        };
        const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
          resolve(false);
          return;
        }

        cognitoUser.getSession((err, session) => {
          if (err || !session || !session.isValid()) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
      } catch (error) {
        console.error('🔄 SESSION: Error validating Cognito session:', error);
        resolve(false);
      }
    });
  }


  private clearSession(): void {
    this.localStorage.remove(NAMESPACE);
  }


  private initializeStores(): void {
    // Dispatch APP.INIT to trigger user location restoration from localStorage

    this.store.dispatch({ type: 'APP.INIT' });
    this.store.dispatch({ type: 'APP-PARAMS.INIT' });
    this.store.dispatch({ type: 'RULES-ENGINE.INIT' });
    this.store.dispatch({ type: 'RULES-ENGINE.INIT_PAGESTATE' });
    this.store.dispatch({ type: 'CONVEYOR-ADMIN.INIT' });
  }


  getSessionUser(): AppSessionUser | null {
    try {
      const local = this.localStorage?.get(NAMESPACE) as AppSessionUser;
      return (local && typeof local === 'object') ? local : null;
    } catch (error) {
      console.error('Error reading session from localStorage:', error);
      return null;
    }
  }


  isAppInitialized(): boolean {
    return this.isInitialized;
  }
}
